import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TaskCard } from "@/components/TaskCard";
import { ComplianceBar } from "@/components/Compliance";
import { STATUS_META, todayISO } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Loader2, Sun, CheckCircle2, Clock, Loader, AlertTriangle } from "lucide-react";

const SUMMARY = [
  { key: "vencida", label: "Vencidas", icon: AlertTriangle, dot: "bg-red-500", text: "text-red-600" },
  { key: "pendiente", label: "Pendientes", icon: Clock, dot: "bg-yellow-500", text: "text-yellow-600" },
  { key: "en_progreso", label: "En progreso", icon: Loader, dot: "bg-blue-500", text: "text-blue-600" },
  { key: "completada", label: "Completadas", icon: CheckCircle2, dot: "bg-emerald-500", text: "text-emerald-600" },
];

export default function EmployeeHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = todayISO();

  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks", {}], queryFn: () => api.get("/tasks").then((r) => r.data) });
  const { data: compliance } = useQuery({ queryKey: ["my-compliance", "mes"], queryFn: () => api.get("/compliance/me?period=mes").then((r) => r.data) });

  const counts = useMemo(() => {
    const c = { pendiente: 0, en_progreso: 0, completada: 0, en_revision: 0, vencida: 0 };
    tasks.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tasks]);

  const todayTasks = tasks
    .filter((t) => t.date === today || (t.status === "vencida"))
    .sort((a, b) => (a.status === "vencida" ? -1 : 1) - (b.status === "vencida" ? -1 : 1) || a.time.localeCompare(b.time));
  const upcoming = tasks.filter((t) => t.date > today && t.status !== "completada").slice(0, 5);

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="employee-home">
      <div className="flex items-center gap-2">
        <Sun className="h-6 w-6 text-amber-500" />
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Hola, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500">Estas son tus tareas de hoy</p>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-4 gap-2">
        {SUMMARY.map((s) => (
          <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm" data-testid={`summary-${s.key}`}>
            <div className={cn("mx-auto mb-1 h-2 w-2 rounded-full", s.dot)} />
            <div className={cn("font-heading text-2xl font-extrabold", s.text)}>{counts[s.key]}</div>
            <div className="text-[10px] font-medium text-slate-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* My compliance */}
      {compliance && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-1 flex items-center justify-between">
            <span className="font-heading text-sm font-bold text-slate-900">Mi cumplimiento (mes)</span>
            <button onClick={() => navigate("/mi-cumplimiento")} className="text-xs font-semibold text-slate-500 underline">Ver detalle</button>
          </div>
          <ComplianceBar pct={compliance.compliance} height="h-3" />
        </div>
      )}

      {/* Today's tasks */}
      <div>
        <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">HOY</h2>
        {todayTasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-400">
            ¡No tienes tareas pendientes para hoy! 🎉
          </div>
        ) : (
          <div className="space-y-3">
            {todayTasks.map((t, i) => <TaskCard key={t.id} task={t} index={i} />)}
          </div>
        )}
      </div>

      {upcoming.length > 0 && (
        <div>
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">Próximas</h2>
          <div className="space-y-3">
            {upcoming.map((t, i) => <TaskCard key={t.id} task={t} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
